import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import type { JobField, JobFieldSet, JobWriteAudit } from '@/api/types'

/**
 * One job's Sage fields — the NWL tab, in the portal.
 *
 * Nothing here is hardcoded. The field list, the labels, the control types and
 * the dropdown values all come from Sage's own Customizer metadata, so a UDF
 * added in Sage shows up here on the next load with no deploy. Which fields may
 * be EDITED is the one thing Sage cannot know, and that lives in a policy table
 * an admin can change.
 *
 * Edits are held locally and saved together. A field-at-a-time save would fire
 * a Sage write on every keystroke-and-blur, and each write is a full row update
 * that can collide with someone editing the job in Sage.
 */
export function JobDetailPage() {
  const { jobNo = '' } = useParams()
  const navigate = useNavigate()

  const [data, setData] = useState<JobFieldSet | null>(null)
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [writes, setWrites] = useState<JobWriteAudit[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [d, w] = await Promise.all([api.jobFields(jobNo), api.jobWrites(jobNo)])
      setData(d)
      setWrites(w)
      setEdits({})
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the job')
    }
  }, [jobNo])

  useEffect(() => {
    void load()
  }, [load])

  const groups = useMemo(() => {
    const by = new Map<string, JobField[]>()
    for (const f of data?.fields ?? []) {
      const list = by.get(f.group) ?? []
      list.push(f)
      by.set(f.group, list)
    }
    return [...by.entries()]
  }, [data])

  const dirty = Object.keys(edits)

  function currentValue(f: JobField): string {
    return edits[f.column] ?? f.value ?? ''
  }

  function setValue(f: JobField, v: string) {
    const original = f.value ?? ''
    setEdits((prev) => {
      const next = { ...prev }
      // Setting a field back to what Sage already holds is not a change, so it
      // must not become a write — every write risks a collision.
      if (v === original) delete next[f.column]
      else next[f.column] = v
      return next
    })
  }

  async function save() {
    if (!dirty.length) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const updated = await api.updateJobFields(jobNo, edits)
      setData(updated)
      setEdits({})

      // Trust the log, not the response. A write Sage discarded because
      // someone had the job open reads back identically from here.
      const w = await api.jobWrites(jobNo)
      setWrites(w)
      const failed = w
        .slice(0, dirty.length)
        .filter((x) => !x.succeeded)
        .map((x) => x.column_name.replace('UDF_', ''))
      if (failed.length) {
        setError(
          `Sage did not keep ${failed.join(', ')}. Someone probably has this ` +
            'job open in Job Maintenance — close it there and save again.',
        )
      } else {
        setNotice(`Saved ${dirty.length} field${dirty.length > 1 ? 's' : ''} to Sage.`)
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  if (!data) {
    return (
      <div className="card">
        {error ? <div className="alert alert-error">{error}</div> : <p className="muted">Loading…</p>}
      </div>
    )
  }

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      <div className="card">
        <div className="crew-heading">
          <h2 className="title" style={{ margin: 0 }}>
            <span className="mono">{data.job_no}</span> · {data.job_description}
          </h2>
          <button className="btn btn-secondary" onClick={() => navigate('/office/jobs')}>
            ← All jobs
          </button>
        </div>
        <p className="muted">
          {[data.city, data.state_code].filter(Boolean).join(', ')} · these fields
          are Sage's, on the <strong>NWL</strong> tab of Job Maintenance. Changes
          write straight back.
        </p>
      </div>

      {groups.map(([group, fields]) => (
        <div className="card" key={group}>
          <h2>{group}</h2>
          <div className="field-grid">
            {fields.map((f) => (
              <div className="form-field" key={f.column} style={{ marginBottom: 0 }}>
                {f.control === 'checkbox' ? (
                  <label className="checkline">
                    <input
                      type="checkbox"
                      checked={currentValue(f) === 'Y'}
                      disabled={!f.editable || busy}
                      onChange={(e) => setValue(f, e.target.checked ? 'Y' : 'N')}
                    />
                    <span>{f.label}</span>
                  </label>
                ) : (
                  <>
                    <label htmlFor={f.column}>{f.label}</label>
                    {f.control === 'select' ? (
                      <select
                        id={f.column}
                        value={currentValue(f)}
                        disabled={!f.editable || busy}
                        onChange={(e) => setValue(f, e.target.value)}
                      >
                        <option value="">—</option>
                        {/* Sage's own validation list, so the portal can never
                            offer a value Job Maintenance would reject. */}
                        {f.options.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id={f.column}
                        type={f.control === 'date' ? 'date' : f.control === 'number' ? 'number' : 'text'}
                        step={f.control === 'number' ? '0.01' : undefined}
                        maxLength={f.control === 'text' ? (f.max_length ?? undefined) : undefined}
                        value={currentValue(f)}
                        disabled={!f.editable || busy}
                        onChange={(e) => setValue(f, e.target.value)}
                      />
                    )}
                  </>
                )}
                {f.note && (
                  <p className="muted" style={{ fontSize: '0.75rem', margin: '0.25rem 0 0' }}>
                    {f.note}
                  </p>
                )}
                {edits[f.column] !== undefined && (
                  <p className="muted" style={{ fontSize: '0.75rem', margin: '0.25rem 0 0' }}>
                    was {f.value || '(blank)'}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Sticky so the count and the button stay reachable on a long form. */}
      <div className="card save-bar">
        <button className="btn" onClick={() => void save()} disabled={!dirty.length || busy}>
          {busy ? 'Saving…' : `Save ${dirty.length || 'no'} change${dirty.length === 1 ? '' : 's'} to Sage`}
        </button>
        {dirty.length > 0 && (
          <button className="btn btn-secondary" onClick={() => setEdits({})} disabled={busy}>
            Discard
          </button>
        )}
      </div>

      {writes.length > 0 && (
        <div className="card">
          <h2>Portal writes to this job</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Field</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Stuck?</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {writes.slice(0, 20).map((w, i) => (
                  <tr key={i}>
                    <td className="mono">{w.written_utc.replace('T', ' ').slice(0, 16)}</td>
                    <td className="mono">{w.column_name.replace('UDF_', '')}</td>
                    <td>{w.old_value ?? '—'}</td>
                    <td>{w.new_value ?? '—'}</td>
                    <td>{w.succeeded ? 'yes' : <strong>NO</strong>}</td>
                    <td>{w.actor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
